import { ButtonProps } from '@amzn/awsui-components-core/button';
import { useRef, useState } from 'react';
import { SnapshotDateField, SnapshotDatePreset } from 'src/components/star-grid/reportSelectors.types';
import { isValidSnapshotDateRange, snapshotDatePreset } from 'src/components/star-grid/utils/versionDateFilter';

const useDateDraft = () => {
  const [draft, setDraft] = useState(() => snapshotDatePreset('last7'));
  const [activeField, setActiveField] = useState<SnapshotDateField>('startDate');
  const setDate = (field: SnapshotDateField, value: string): void => {
    setDraft((current) => ({ ...current, preset: 'custom', [field]: value }));
    setActiveField(field);
  };
  const chooseDate = (value: string): void => {
    if (activeField === 'endDate') return setDate('endDate', value);
    setDraft({ preset: 'custom', startDate: value, endDate: '' });
    setActiveField('endDate');
  };
  const calendarDate = draft[activeField] || draft.startDate;
  return { draft, setDraft, activeField, setActiveField, setDate, chooseDate, calendarDate };
};

export const useVersionDateFilter = () => {
  const [range, setRange] = useState(() => snapshotDatePreset('last7'));
  const [editing, setEditing] = useState(false);
  const draftState = useDateDraft();
  const triggerRef = useRef<ButtonProps.Ref>(null);
  const valid = isValidSnapshotDateRange(draftState.draft);
  const hasError = !valid && !(draftState.activeField === 'endDate' && !draftState.draft.endDate);
  const open = (): void => {
    draftState.setDraft(range);
    draftState.setActiveField('startDate');
    setEditing(true);
  };
  const cancel = (): void => setEditing(false);
  const dismiss = (): void => {
    setEditing(false);
    triggerRef.current?.focus();
  };
  const apply = (): void => {
    if (!valid) return;
    setRange({ ...draftState.draft });
    dismiss();
  };
  const setPreset = (preset: SnapshotDatePreset): void => {
    setRange(snapshotDatePreset(preset));
    setEditing(false);
  };
  const clear = (): void => {
    setRange(snapshotDatePreset('all'));
    dismiss();
  };
  return { range, editing, valid, hasError, triggerRef, open, cancel, dismiss, apply, setPreset, clear, ...draftState };
};
